// components/MemberPortal/VerificationPending.js
import React, { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../../axiosConfig';

const VerificationPending = ({ memberProfile }) => {
  const method = memberProfile?.verification_method === 'document' ? 'document' : 'live_call';
  const methodLabel = method === 'document' ? 'Document Upload' : 'Video Call';

  const [callInfo, setCallInfo] = useState(null);
  const [callLoading, setCallLoading] = useState(false);
  const [callError, setCallError] = useState(null);

  const fetchMyCall = useCallback(async () => {
    if (method !== 'live_call') return;
    setCallLoading(true);
    setCallError(null);
    try {
      const { data } = await axiosInstance.get('/verification/my-call/');
      setCallInfo(data || null);
    } catch (err) {
      console.error(err);
      setCallError('Unable to load your scheduled call at the moment.');
    } finally {
      setCallLoading(false);
    }
  }, [method]);

  useEffect(() => {
    fetchMyCall();
  }, [fetchMyCall]);

  const formatCallTime = (isoString, tz) => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'full',
        timeStyle: 'short',
        timeZone: tz || undefined,
      }).format(new Date(isoString));
    } catch {
      return new Date(isoString).toLocaleString();
    }
  };

  return (
    <div className="verification-pending">
      <div className="status-header">
        <div className="status-icon pending">⏰</div>
        <h2>Verification In Progress</h2>
        <p>We're reviewing your membership verification.</p>
      </div>

      <div className="status-details">
        <div className="detail-item">
          <strong>Home Club</strong>
          <span className="value">{memberProfile.home_club?.name || '—'}</span>
        </div>

        <div className="detail-item">
          <strong>Verification Method</strong>
          <span className={`method-chip ${method}`}>
            <span className="dot" />
            {methodLabel}
          </span>
        </div>

        <div className="detail-item">
          <strong>Submitted</strong>
          <span className="value">
            {new Date(memberProfile.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Live Call details (fetched from /verification/my-call/) */}
      {method === 'live_call' && (
        <div className="call-section">
          <h3>Your Verification Call</h3>

          {callLoading && (
            <div className="call-card loading">
              <div className="spinner-sm" />
              <span>Loading your call details…</span>
            </div>
          )}

          {!callLoading && callError && (
            <div className="call-card error">
              <div className="error-icon">⚠️</div>
              <div>
                <div className="error-title">Couldn’t load call info</div>
                <div className="error-text">{callError}</div>
              </div>
              <button className="piq-btn-secondary" onClick={fetchMyCall}>Retry</button>
            </div>
          )}

          {!callLoading && !callError && callInfo && callInfo.scheduled === true && (
            <div className="call-card">
              <div className="call-row">
                <div className="call-label">When</div>
                <div className="call-value">
                  {formatCallTime(callInfo.start_time_local, callInfo.timezone)}
                  {callInfo.timezone ? <span className="tz"> · {callInfo.timezone}</span> : null}
                </div>
              </div>
              <div className="call-row">
                <div className="call-label">Duration</div>
                <div className="call-value">{callInfo.duration_minutes} min</div>
              </div>
              {callInfo.zoom_join_url ? (
                <div className="call-actions">
                  <a
                    href={callInfo.zoom_join_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="piq-btn-primary"
                  >
                    Join Zoom
                  </a>
                  <button className="piq-btn-ghost" onClick={fetchMyCall}>
                    Refresh
                  </button>
                </div>
              ) : (
                <div className="call-note">Your Zoom link will appear here once generated.</div>
              )}
            </div>
          )}

          {!callLoading && !callError && callInfo && callInfo.scheduled === false && (
            <div className="call-card">
              <div className="call-note">
                We haven’t detected a scheduled call yet. If you didn’t get a calendar invite,
                please check your spam folder or contact support.
              </div>
              <button className="piq-btn-ghost" onClick={fetchMyCall}>Check Again</button>
            </div>
          )}
        </div>
      )}

      <div className="next-steps">
        <h3>What's Next?</h3>
        <p>
          {method === 'document'
            ? 'Our team will review your documents within 24–48 hours. You’ll receive an email once verification is complete.'
            : 'You should receive a calendar invitation for your verification call. Please check your email.'}
        </p>
      </div>

      <div className="help-section">
        <p>Questions about your verification?</p>
        <a href="mailto:support@privateiq.golf" className="support-link">
          Contact Support
        </a>
      </div>
    </div>
  );
};

export default VerificationPending;
